import { useState, useRef, useCallback } from 'react';
import { Shape } from '../types/Shape';

export interface UseCanvasArrowsReturn {
  // Connection state
  isConnecting: boolean;
  setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
  snappedPoint: { x: number; y: number } | null;
  setSnappedPoint: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  hoveredShapeId: string | null;
  setHoveredShapeId: React.Dispatch<React.SetStateAction<string | null>>;

  // Endpoint dragging state
  isDraggingArrowEndpoint: boolean;
  setIsDraggingArrowEndpoint: React.Dispatch<React.SetStateAction<boolean>>;

  // Mouse position (for arrow preview)
  mousePos: { x: number; y: number } | null;
  setMousePos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;

  // Nearby shapes for connection
  nearbyShapeIds: string[];
  setNearbyShapeIds: React.Dispatch<React.SetStateAction<string[]>>;
  nearbyShapeIdsRef: React.MutableRefObject<string[]>;

  // Utility functions
  clearArrowState: () => void;
}

export function useCanvasArrows(): UseCanvasArrowsReturn {
  // Connection state
  const [isConnecting, setIsConnecting] = useState(false);
  const [snappedPoint, setSnappedPoint] = useState<{ x: number; y: number } | null>(null);
  const [hoveredShapeId, setHoveredShapeId] = useState<string | null>(null);

  // Endpoint dragging state
  const [isDraggingArrowEndpoint, setIsDraggingArrowEndpoint] = useState(false);

  // Mouse position (for arrow preview)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  // Nearby shapes for connection
  const [nearbyShapeIds, setNearbyShapeIds] = useState<string[]>([]);
  const nearbyShapeIdsRef = useRef<string[]>([]);

  // Clear all arrow-related state
  const clearArrowState = useCallback(() => {
    setIsConnecting(false);
    setSnappedPoint(null);
    setHoveredShapeId(null);
    setIsDraggingArrowEndpoint(false);
    setMousePos(null);
    setNearbyShapeIds([]);
    nearbyShapeIdsRef.current = [];
  }, []);

  return {
    // Connection state
    isConnecting,
    setIsConnecting,
    snappedPoint,
    setSnappedPoint,
    hoveredShapeId,
    setHoveredShapeId,

    // Endpoint dragging state
    isDraggingArrowEndpoint,
    setIsDraggingArrowEndpoint,

    // Mouse position
    mousePos,
    setMousePos,

    // Nearby shapes
    nearbyShapeIds,
    setNearbyShapeIds,
    nearbyShapeIdsRef,

    // Utility functions
    clearArrowState,
  };
}
